/**
 * Group presence proof (#2114): the Präsenznachweis of one group (JSON for the page,
 * PDF and XLSX downloads) and the admin corrections.
 *
 * The downloads stream a file, so they use a raw `fetch` with a bearer token like the
 * Aufsichts-PDF download (`useAttendanceReportPdf`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLingui } from '@lingui/react';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';

import { GroupPresenceService } from '@/api/services/GroupPresenceService';
import type { group_presence_PresenceReportResponse } from '@/api/models/group_presence_PresenceReportResponse';
import type { group_presence_GroupPresenceSegmentResponse } from '@/api/models/group_presence_GroupPresenceSegmentResponse';
import { useAuthContext } from '@/contexts/AuthContext';
import { retryUnlessClientError } from '@/lib/queryRetry';
import { formatLocalDate } from '@/utils/dateUtils';

export type PresenceReport = group_presence_PresenceReportResponse;
export type PresenceReportDay = PresenceReport['days'][number];
export type PresenceReportEmployee = PresenceReportDay['employees'][number];
export type PresenceReportSegment = PresenceReportEmployee['segments'][number];

export const groupPresenceKeys = {
  all: ['group-presence'] as const,
  report: (groupId: string, from: string, to: string) =>
    [...groupPresenceKeys.all, 'report', groupId, { from, to }] as const,
};

/** The Präsenznachweis of one group for a range of days (one day for the page). */
export function useGroupPresenceReport(groupId: string | undefined, from: Date, to: Date) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const fromKey = formatLocalDate(from);
  const toKey = formatLocalDate(to);
  return useQuery({
    queryKey: groupPresenceKeys.report(groupId ?? '', fromKey, toKey),
    queryFn: () => GroupPresenceService.getApiV1GroupsPresenceReport(groupId as string, fromKey, toKey),
    enabled: !!groupId && !!isSignedIn && isAuthReady,
    retry: retryUnlessClientError,
    refetchOnWindowFocus: true,
  });
}

export interface AddPresenceInput {
  groupId: string;
  employeeId: string;
  startedAt: string;
  endedAt?: string;
}

export interface UpdatePresenceInput {
  id: string;
  startedAt?: string;
  endedAt?: string;
}

/** The three corrections share one invalidation: every report of every group. */
function useInvalidateReports() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: groupPresenceKeys.all });
}

export function useAddGroupPresence() {
  const invalidate = useInvalidateReports();
  return useMutation<group_presence_GroupPresenceSegmentResponse, unknown, AddPresenceInput>({
    mutationFn: ({ groupId, employeeId, startedAt, endedAt }) =>
      GroupPresenceService.postApiV1GroupsPresence(groupId, {
        employee_id: employeeId,
        started_at: startedAt,
        ended_at: endedAt,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateGroupPresence() {
  const invalidate = useInvalidateReports();
  return useMutation<group_presence_GroupPresenceSegmentResponse, unknown, UpdatePresenceInput>({
    mutationFn: ({ id, startedAt, endedAt }) =>
      GroupPresenceService.patchApiV1Presence(id, { started_at: startedAt, ended_at: endedAt }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteGroupPresence() {
  const invalidate = useInvalidateReports();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => GroupPresenceService.deleteApiV1Presence(id),
    onSuccess: () => invalidate(),
  });
}

export type PresenceReportFormat = 'pdf' | 'xlsx';

export interface DownloadPresenceReportParams {
  groupId: string;
  from: Date;
  to: Date;
}

/** A failed download with the HTTP status and the server's JSON body. */
export class PresenceReportDownloadError extends Error {
  readonly status: number;
  readonly body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'PresenceReportDownloadError';
    this.status = status;
    this.body = body;
  }
}

async function downloadPresenceReport(
  format: PresenceReportFormat,
  { groupId, from, to }: DownloadPresenceReportParams,
  fallbackMessage: (status: number) => string,
): Promise<{ blob: Blob; filename: string }> {
  const { getAuthToken } = await import('@/api/auth-helper');
  const token = await getAuthToken();
  const baseUrl = import.meta.env.VITE_API_BASE || '';
  const query = `from=${formatLocalDate(from)}&to=${formatLocalDate(to)}&lang=de`;
  const response = await fetch(
    `${baseUrl}/api/v1/groups/${encodeURIComponent(groupId)}/presence-report/${format}?${query}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'message' in body && typeof body.message === 'string' && body.message
        ? body.message
        : fallbackMessage(response.status);
    throw new PresenceReportDownloadError(message, response.status, body ?? undefined);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || `praesenznachweis.${format}`;
  return { blob, filename };
}

function saveBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/** Downloads the Präsenznachweis in one format; the dialog shows a failure inline. */
export function useDownloadPresenceReport(format: PresenceReportFormat) {
  const { i18n } = useLingui();
  return useMutation<{ blob: Blob; filename: string }, PresenceReportDownloadError, DownloadPresenceReportParams>({
    mutationFn: (params) =>
      downloadPresenceReport(format, params, (status) =>
        i18n._({ id: 'presence.export.httpError', message: 'Download failed (HTTP {status})', values: { status } }),
      ),
    onSuccess: ({ blob, filename }) => {
      saveBlob(blob, filename);
      toast.success(
        format === 'xlsx'
          ? i18n._({ id: 'presence.export.successExcel', message: 'Excel file downloaded' })
          : i18n._({ id: 'presence.export.successPdf', message: 'PDF downloaded' }),
      );
    },
  });
}

export const openPresenceKeys = {
  all: [...groupPresenceKeys.all, 'open'] as const,
  list: (orgId: string | null | undefined) => [...openPresenceKeys.all, orgId ?? null] as const,
};

/**
 * Who is in which group right now (#2114, `GET /presence/open`), for the
 * Dashboard's "On shift now" row (#2175 C5). `group_presence_enabled` is false
 * while the organisation does not record group presence; the list is then empty.
 */
export function useOpenGroupPresence(options?: { enabled?: boolean }) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  return useQuery({
    queryKey: openPresenceKeys.list(orgId),
    queryFn: () => GroupPresenceService.getApiV1PresenceOpen(),
    enabled: (options?.enabled ?? true) && !!isSignedIn && isAuthReady,
    retry: retryUnlessClientError,
    staleTime: 60 * 1000,
  });
}
